import React from "react";
import Link from "next/link";
import SiteQuickLinks from "./site-quick-links";

const TRUST = {
  en: [
    {
      image: "/images/V10/v10_single.svg",
      title: "Hydraulics",
      text: "Pumps, motors, valves, and manifolds for mobile and industrial systems.",
      href: "/en/products",
    },
    {
      image: "/images/electrical/emerald-drive.png",
      title: "Electrical",
      text: "Servo drives, controllers, and motion solutions for automation.",
      href: "/en/products/electrical",
    },
    {
      image: "/images/mechanical/linear-guides.png",
      title: "Mechanical",
      text: "Linear bearings, ball screws, and precision components.",
      href: "/en/products/mechanical",
    },
    {
      image: "/images/new-courel/section_0/tu-world2.svg",
      title: "Global support",
      text: "Technical sales and export from the USA to partners worldwide.",
      href: "/en/contact-us",
    },
  ],
  es: [
    {
      image: "/images/V10/v10_single.svg",
      title: "Hidráulica",
      text: "Bombas, motores, válvulas y colectores para sistemas móviles e industriales.",
      href: "/es/productos",
    },
    {
      image: "/images/electrical/emerald-drive.png",
      title: "Eléctrico",
      text: "Servo drives, controladores y soluciones de movimiento.",
      href: "/es/productos",
    },
    {
      image: "/images/mechanical/linear-guides.png",
      title: "Mecánico",
      text: "Guías lineales, husillos de bolas y componentes de precisión.",
      href: "/es/productos/mecanicos/guias-lineales",
    },
    {
      image: "/images/new-courel/section_0/tu-world2.svg",
      title: "Soporte global",
      text: "Ventas técnicas y exportación desde EE. UU. a socios en todo el mundo.",
      href: "/es/contactenos",
    },
  ],
};

const INTRO = {
  en: {
    p1: (
      <>
        <strong>Technical Union</strong> is an independent industrial sales
        representative connecting buyers with manufacturers of hydraulic,
        pneumatic, electrical, and mechanical components.
      </>
    ),
    p2: (
      <>
        We help identify products, find replacement alternatives, and access
        technical information, pricing, and supply options through the
        manufacturers we work with.
      </>
    ),
    whyTitle: "Why Work with Technical Union?",
    reasons: [
      {
        title: "Access to U.S. Manufacturers",
        text: "Check products, pricing, and purchasing options through participating manufacturers.",
      },
      {
        title: "Alternatives and Replacement Parts",
        text: "Identify possible replacements using part numbers, specifications, and application requirements.",
      },
      {
        title: "Technical Support",
        text: "Access technical information and guidance to identify components suited to your machinery.",
      },
      {
        title: "International Supply Options",
        text: "Explore U.S. inventory availability and shipping alternatives depending on the manufacturer and the product.",
      },
    ],
    lookupTitle: "Looking for a replacement part or a specific component?",
    lookupText:
      "Send us the part number, brand, machine model, or the component specifications. We will help you identify options through our network of manufacturers.",
    lookupCta: "Inquire about a product or request a quote",
    lookupHref: "/en/contact-us",
    makersTitle:
      "Are You a U.S. Industrial Manufacturer Looking to Expand Your Market?",
    makersP1:
      "Technical Union works with manufacturers of industrial components to develop sales opportunities and build commercial relationships across Latin America.",
    makersP2:
      "We help connect manufacturers with potential distributors, OEMs, machinery builders, and industrial buyers seeking reliable products and competitive alternatives.",
    makersHelp: "How we can help:",
    makersPoints: [
      "Develop relationships with potential distributors and industrial customers.",
      "Increase product visibility in Latin America.",
      "Promote available products, replacement solutions, and U.S. inventory.",
      "Identify market opportunities and connect manufacturers with prospective buyers.",
    ],
    makersKicker: "For Manufacturers",
    makersAsk: "Interested in developing sales opportunities in Latin America?",
    makersCta: "Discuss a Partnership with TU →",
    makersHref: "/en/contact-us",
  },
  es: {
    p1: (
      <>
        <strong>Technical Union</strong> es un representante de ventas industrial
        independiente que conecta a compradores con fabricantes de componentes
        hidráulicos, neumáticos, eléctricos y mecánicos.
      </>
    ),
    p2: (
      <>
        Ayudamos a identificar productos, encontrar alternativas de reemplazo y
        acceder a información técnica, precios y opciones de suministro a través
        de los fabricantes con los que trabajamos.
      </>
    ),
    whyTitle: "¿Por qué trabajar con Technical Union?",
    reasons: [
      {
        title: "Acceso a fabricantes de EE. UU.",
        text: "Consulte productos, precios y opciones de compra a través de fabricantes participantes.",
      },
      {
        title: "Alternativas y repuestos",
        text: "Identifique posibles reemplazos mediante números de parte, especificaciones y requisitos de aplicación.",
      },
      {
        title: "Soporte técnico",
        text: "Acceda a información técnica y orientación para identificar componentes adecuados para su maquinaria.",
      },
      {
        title: "Opciones de suministro internacional",
        text: "Explore disponibilidad de inventario en EE. UU. y alternativas de envío según el fabricante y el producto.",
      },
    ],
    lookupTitle: "¿Busca un repuesto o un componente específico?",
    lookupText:
      "Envíenos el número de parte, la marca, el modelo de la máquina o las especificaciones del componente. Le ayudaremos a identificar opciones a través de nuestra red de fabricantes.",
    lookupCta: "Consultar un producto o solicitar cotización",
    lookupHref: "/es/contactenos",
    makersTitle:
      "¿Es usted un fabricante industrial de EE. UU. que busca ampliar su mercado?",
    makersP1:
      "Technical Union trabaja con fabricantes de componentes industriales para desarrollar oportunidades de venta y construir relaciones comerciales en Latinoamérica.",
    makersP2:
      "Ayudamos a conectar a los fabricantes con posibles distribuidores, OEM, constructores de maquinaria y compradores industriales que buscan productos confiables y alternativas competitivas.",
    makersHelp: "Cómo podemos ayudar:",
    makersPoints: [
      "Desarrollar relaciones con posibles distribuidores y clientes industriales.",
      "Aumentar la visibilidad de los productos en Latinoamérica.",
      "Promover productos disponibles, soluciones de reemplazo e inventario en EE. UU.",
      "Identificar oportunidades de mercado y conectar a los fabricantes con compradores potenciales.",
    ],
    makersKicker: "Para fabricantes",
    makersAsk: "¿Le interesa desarrollar oportunidades de venta en Latinoamérica?",
    makersCta: "Conversar sobre una alianza con TU →",
    makersHref: "/es/contactenos",
  },
};

export default function HomePageContent({ locale = "en" }) {
  const copy = INTRO[locale] || INTRO.en;
  const trust = TRUST[locale] || TRUST.en;

  return (
    <>
      <section className="tu-home-intro">
        <div className="tu-container">
          <div className="tu-home-intro__copy">
            <p className="maintext">{copy.p1}</p>
            <p className="maintext tu-home-intro__p2">{copy.p2}</p>
          </div>
        </div>
      </section>

      {copy.reasons ? (
        <section className="tu-home-why" aria-labelledby="tu-home-why-title">
          <div className="tu-container">
            <h2 id="tu-home-why-title" className="tu-home-why__title">
              {copy.whyTitle}
            </h2>
            <div className="tu-home-why__grid">
              {copy.reasons.map((reason, index) => (
                <article key={reason.title} className="tu-home-why__item">
                  <span className="tu-home-why__num">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3>{reason.title}</h3>
                  <p>{reason.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="tu-trust" aria-labelledby="tu-trust-heading">
        <div className="tu-container">
          <h2 id="tu-trust-heading" className="tu-trust__heading">
            {locale === "es" ? "Áreas de especialización" : "Areas of expertise"}
          </h2>
          <div className="tu-trust__grid">
            {trust.map((card) => (
              <article key={card.title} className="tu-trust__item">
                <Link href={card.href} className="tu-trust__card">
                  <span className="tu-trust__photo">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={card.image} alt="" width={320} height={180} decoding="async" loading="lazy" />
                  </span>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {copy.lookupTitle ? (
        <section className="tu-home-lookup" aria-labelledby="tu-home-lookup-title">
          <div className="tu-container">
            <h2 id="tu-home-lookup-title" className="tu-home-lookup__title">
              {copy.lookupTitle}
            </h2>
            <p>{copy.lookupText}</p>
            <Link href={copy.lookupHref} className="tu-btn tu-home-lookup__cta">
              {copy.lookupCta}
            </Link>
          </div>
        </section>
      ) : null}

      {copy.makersTitle ? (
        <section className="tu-home-makers" aria-labelledby="tu-home-makers-title">
          <div className="tu-container">
            <div className="tu-home-makers__copy">
              <h2 id="tu-home-makers-title" className="tu-home-makers__title">
                {copy.makersTitle}
              </h2>
              <p>{copy.makersP1}</p>
              <p>{copy.makersP2}</p>
              <h3 className="tu-home-makers__help">{copy.makersHelp}</h3>
              <ul>
                {copy.makersPoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <p className="tu-home-makers__kicker">{copy.makersKicker}</p>
              <p className="tu-home-makers__ask">{copy.makersAsk}</p>
              <Link href={copy.makersHref} className="tu-btn tu-btn-primary">
                {copy.makersCta}
              </Link>
            </div>
          </div>
        </section>
      ) : null}

      <section className="tu-home-quick-links">
        <div className="tu-container">
          <SiteQuickLinks locale={locale} />
        </div>
      </section>
    </>
  );
}
